import type { ReactNode } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';
import { Logo } from '../../ui';
import { ONBOARDING_STEPS, stepMetadata, useOnboarding, useAuth, type OnboardingStep } from '../../../stores';

interface OnboardingLayoutProps {
  children: ReactNode;
  currentStep: OnboardingStep;
  currentStepIndex: number;
  onBack?: () => void;
  showBackButton?: boolean;
}

// Progress dots component
function ProgressIndicator({
  currentIndex,
  totalSteps,
}: {
  currentIndex: number;
  totalSteps: number;
}) {
  return (
    <div className="flex items-center gap-2">
      {Array.from({ length: totalSteps }).map((_, index) => {
        const isComplete = index < currentIndex;
        const isCurrent = index === currentIndex;
        const step = ONBOARDING_STEPS[index];
        const meta = stepMetadata[step];

        return (
          <div key={step} className="group relative">
            <motion.div
              className={`h-2 rounded-full transition-all duration-500 ${
                isCurrent
                  ? 'w-8 bg-gradient-to-r from-accent-500 to-accent-400'
                  : isComplete
                    ? 'w-2 bg-accent-400'
                    : 'w-2 bg-surface-300 dark:bg-surface-600'
              }`}
              initial={{ scale: 0.8, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              transition={{ delay: index * 0.05, duration: 0.3 }}
            />
            {/* Tooltip on hover */}
            <div className="absolute bottom-full left-1/2 mb-2 -translate-x-1/2 opacity-0 transition-opacity group-hover:opacity-100 pointer-events-none">
              <div className="whitespace-nowrap rounded-lg bg-surface-800 px-3 py-1.5 text-xs text-white shadow-lg dark:bg-surface-700">
                {meta.title}
              </div>
            </div>
          </div>
        );
      })}
    </div>
  );
}

// Page transition variants
const pageVariants = {
  initial: {
    opacity: 0,
    y: 20,
    scale: 0.98,
  },
  animate: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.5,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
  exit: {
    opacity: 0,
    y: -10,
    scale: 0.98,
    transition: {
      duration: 0.3,
      ease: [0.22, 1, 0.36, 1] as const,
    },
  },
};

export function OnboardingLayout({
  children,
  currentStep,
  currentStepIndex,
  onBack,
  showBackButton = true,
}: OnboardingLayoutProps) {
  const navigate = useNavigate();
  const { reset } = useOnboarding();
  const { logout } = useAuth();
  const totalSteps = ONBOARDING_STEPS.length;
  const stepInfo = stepMetadata[currentStep];
  const isDev = import.meta.env.DEV;

  const handleDevExit = () => {
    reset();
    logout();
    navigate('/login');
  };

  return (
    <div className="min-h-screen bg-surface-50 dark:bg-surface-900 relative overflow-hidden">
      {/* Ambient background gradients */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        {/* Warm radial glow - top right */}
        <div
          className="absolute -top-1/4 -right-1/4 w-1/2 h-1/2 rounded-full opacity-30 dark:opacity-20"
          style={{
            background:
              'radial-gradient(circle, rgba(163, 139, 109, 0.4) 0%, transparent 70%)',
          }}
        />
        {/* Teal accent glow - bottom left */}
        <div
          className="absolute -bottom-1/4 -left-1/4 w-1/2 h-1/2 rounded-full opacity-20 dark:opacity-15"
          style={{
            background:
              'radial-gradient(circle, rgba(20, 184, 166, 0.3) 0%, transparent 70%)',
          }}
        />
        {/* Subtle texture overlay */}
        <div
          className="absolute inset-0 opacity-[0.02] dark:opacity-[0.03]"
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`,
          }}
        />
      </div>

      {/* Header */}
      <header className="relative z-10 px-6 py-4 sm:px-8">
        <div className="mx-auto max-w-2xl flex items-center justify-between">
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <Logo variant="wordmark" size="sm" />
          </motion.div>

          <motion.div
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.5, delay: 0.1 }}
          >
            <ProgressIndicator currentIndex={currentStepIndex} totalSteps={totalSteps} />
          </motion.div>
        </div>
      </header>

      {/* Main content */}
      <main className="relative z-10 px-6 pb-24">
        <div className="mx-auto max-w-2xl">
          {/* Step header */}
          <motion.div
            className="mb-8 text-center"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <p className="text-sm font-medium uppercase tracking-wider text-accent-600 dark:text-accent-400 mb-2">
              Step {currentStepIndex + 1} of {totalSteps}
            </p>
            <h1 className="font-display text-3xl sm:text-4xl font-semibold text-surface-900 dark:text-white">
              {stepInfo.title}
            </h1>
            <p className="mt-2 text-surface-600 dark:text-surface-400">{stepInfo.subtitle}</p>
          </motion.div>

          {/* Step content with animation */}
          <AnimatePresence mode="wait">
            <motion.div
              key={currentStep}
              variants={pageVariants}
              initial="initial"
              animate="animate"
              exit="exit"
            >
              {children}
            </motion.div>
          </AnimatePresence>

          {/* Back button */}
          {showBackButton && currentStepIndex > 0 && onBack && (
            <motion.div
              className="mt-8 text-center"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              transition={{ delay: 0.4 }}
            >
              <button
                onClick={onBack}
                className="text-sm text-surface-500 hover:text-surface-700 dark:text-surface-400 dark:hover:text-surface-200 transition-colors"
              >
                ← Go back
              </button>
            </motion.div>
          )}
        </div>
      </main>

      {/* Crisis helpline footer - always visible */}
      <footer className="fixed bottom-0 left-0 right-0 z-10 bg-surface-50/80 dark:bg-surface-900/80 backdrop-blur-sm border-t border-surface-200 dark:border-surface-700 px-6 py-3">
        <div className="mx-auto max-w-2xl text-center">
          <p className="text-xs text-surface-500 dark:text-surface-400">
            In crisis?{' '}
            <a href="tel:988" className="font-medium text-accent-600 dark:text-accent-400 hover:underline">
              Call 988
            </a>{' '}
            (Suicide & Crisis Lifeline) or{' '}
            <a href="tel:1-800-662-4357" className="font-medium text-accent-600 dark:text-accent-400 hover:underline">
              1-800-662-4357
            </a>{' '}
            (SAMHSA)
          </p>
          {isDev && (
            <button
              onClick={handleDevExit}
              className="mt-2 text-xs text-red-500 hover:text-red-700 underline"
            >
              Dev: Exit to Login
            </button>
          )}
        </div>
      </footer>
    </div>
  );
}
